import { retry } from '@worldcoo/shared/utils';

declare global {
	interface Window {
		grecaptcha?: {
			ready: (cb: () => void) => void;
			execute: (captchaKey: string, opts: { action: 'submit' }) => Promise<string | undefined>;
		};
	}
}

function resolveSiteKey(siteKey?: string): string | undefined {
	return siteKey ?? import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY;
}

export type GenerateCaptchaTokenError = 'INVALID_CAPTCHA' | 'CAPTCHA_NOT_LOADED' | 'INVALID_CAPTCHA_KEY_OR_ORIGIN';

type GenerateCaptchaTokenResult = { data: string; error: null } | { data: null; error: GenerateCaptchaTokenError };

export async function generateCaptchaTokenResult(siteKey?: string): Promise<GenerateCaptchaTokenResult> {
	if (typeof window === 'undefined') return { data: null, error: 'CAPTCHA_NOT_LOADED' };

	// The reCAPTCHA script is injected with `defer`, so `window.grecaptcha` may not exist yet the first
	// time a token is requested (common for bots and slow connections that reach a form before the script
	// loads — the source of "[WARN] Google Recaptcha not loaded" errors). Retry with backoff until it
	// appears so a transient not-loaded-yet state recovers instead of failing the action.
	const grecaptcha = await retry({ attempts: 6, delayInMs: 50 }, () => {
		if (!window.grecaptcha) throw new Error('reCAPTCHA not loaded yet');
		return window.grecaptcha;
	}).catch(() => undefined);
	if (!grecaptcha) return { data: null, error: 'CAPTCHA_NOT_LOADED' };

	const key = resolveSiteKey(siteKey);
	if (key === undefined) return { data: null, error: 'INVALID_CAPTCHA_KEY_OR_ORIGIN' };

	return new Promise<GenerateCaptchaTokenResult>((resolve) => {
		grecaptcha.ready(() => {
			grecaptcha
				.execute(key, { action: 'submit' })
				.then((captchaToken) => {
					resolve(captchaToken ? { data: captchaToken, error: null } : { data: null, error: 'INVALID_CAPTCHA' });
				})
				.catch(() => resolve({ data: null, error: 'INVALID_CAPTCHA' }));
		});
	});
}
